import React, { useEffect, useRef } from "react";

const TacticalVisualizer = ({ atmosphere, color }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = canvas.width = 1000;
    let h = canvas.height = 1000;
    let frame = 0, raf;
    const cx = w / 2, cy = h / 2;

    // Parse hex color to rgba components for flexible usage
    const hexToRgb = (hex) => {
        let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : {r:255,g:255,b:255};
    };
    const rgb = hexToRgb(color);
    const rgba = (a) => `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;

    // --- PRE-COMPUTE: GLOBE DOTS (SIGNALS / MEDIA MONITORING) ---
    const dots = [];
    const signalsArcs = [];
    const makeArc = (dotsArr) => {
        const landDots = dotsArr.filter(d=>d.land);
        const arcColors = ["14,165,233","99,102,241","16,185,129","245,158,11","244,63,94"];
        let s, e, tries=0;
        do {
            s = landDots[Math.floor(Math.random()*landDots.length)];
            e = landDots[Math.floor(Math.random()*landDots.length)];
            tries++;
        } while((!s||!e||s===e) && tries<30);
        return { start:s||dotsArr[0], end:e||dotsArr[1], progress:Math.random(), speed:0.002+Math.random()*0.003, history:[], color:arcColors[Math.floor(Math.random()*arcColors.length)] };
    };

    if (atmosphere === 'SIGNALS') {
        const isLand = (lat, lon) => {
            if (lat > 15 && lat < 70 && lon > -165 && lon < -55) return true;
            if (lat > 35 && lat < 75 && lon > -15 && lon < 40)   return true;
            if (lat > -10 && lat < 75 && lon > 50 && lon < 170)  return true;
            if (lat > -35 && lat < 30 && lon > -20 && lon < 50)  return true;
            return false;
        };
        for (let i = 0; i < 3000; i++) {
            let z = Math.random()*2-1, r = Math.sqrt(1-z*z), th = Math.random()*Math.PI*2;
            let x = r*Math.cos(th), y = r*Math.sin(th);
            let lat = Math.asin(y)*180/Math.PI, lon = Math.atan2(x,z)*180/Math.PI;
            dots.push({ x, y, z, land: isLand(lat,lon), isHub:false, size:1.2, hubColor:"" });
        }
        // Mark 48 hub nodes on land dots
        const hubColors = ["16,185,129","56,189,248","99,102,241","245,158,11","232,121,249","244,63,94","6,182,212","139,92,246","20,184,166","251,191,36"];
        const landDots = dots.filter(d=>d.land);
        for(let i=0; i<48; i++) {
            let hd = landDots[Math.floor(Math.random()*landDots.length)];
            if(hd) { hd.isHub=true; hd.size=2.8; hd.hubColor=hubColors[i%hubColors.length]; }
        }
        // 120 shooting-star arcs pre-seeded
        for(let i=0; i<120; i++) signalsArcs.push(makeArc(dots));
    }

    // --- PRE-COMPUTE: 3D CUBE NODES (CRYSTAL / TRAINING) ---
    const cubeNodes = [];
    if (atmosphere === 'CRYSTAL') {
        for(let i=-1; i<=1; i+=2) for(let j=-1; j<=1; j+=2) for(let k=-1; k<=1; k+=2)
            cubeNodes.push({x:i, y:j, z:k});
    }

    // (state arrays removed — new animations are stateless per-frame)

    const animate = () => {
        ctx.clearRect(0,0,w,h);
        frame++;

        // =============================================================
        // 1. MEDIA MONITORING → PREMIUM INTELLIGENCE GLOBE  (SIGNALS)
        //    Full port of the admin login globe — colored hubs, arcs, scan
        // =============================================================
        if (atmosphere === 'SIGNALS') {
            const rotation = frame * 0.0015;
            const tilt = 0.35;
            const rad = Math.min(w, h) * 0.38;
            const time = Date.now();

            // Globe soft glow (dark atmosphere version)
            const gGrad = ctx.createRadialGradient(cx,cy,rad*0.3, cx,cy,rad*1.15);
            gGrad.addColorStop(0, rgba(0.08)); gGrad.addColorStop(1, "transparent");
            ctx.fillStyle = gGrad; ctx.beginPath(); ctx.arc(cx,cy,rad*1.15,0,Math.PI*2); ctx.fill();

            // Backbone network (dashed lines between hubs)
            ctx.setLineDash([4,6]); ctx.strokeStyle = rgba(0.2); ctx.lineWidth = 0.8;
            ctx.beginPath();
            for(let i=0; i<dots.filter(d=>d.isHub).length; i++) {
                let h1 = dots.filter(d=>d.isHub)[i];
                for(let j=i+1; j<Math.min(i+4, dots.filter(d=>d.isHub).length); j++) {
                    let h2 = dots.filter(d=>d.isHub)[j];
                    let rz1 = h1.x*Math.sin(rotation)+h1.z*Math.cos(rotation);
                    let tz1 = h1.y*Math.sin(tilt)+rz1*Math.cos(tilt);
                    let rz2 = h2.x*Math.sin(rotation)+h2.z*Math.cos(rotation);
                    let tz2 = h2.y*Math.sin(tilt)+rz2*Math.cos(tilt);
                    if(tz1<0.2 && tz2<0.2) {
                        let sc1=300/(300+tz1*150), sc2=300/(300+tz2*150);
                        ctx.moveTo(cx+(h1.x*Math.cos(rotation)-h1.z*Math.sin(rotation))*rad*sc1, cy+(h1.y*Math.cos(tilt)-rz1*Math.sin(tilt))*rad*sc1);
                        ctx.lineTo(cx+(h2.x*Math.cos(rotation)-h2.z*Math.sin(rotation))*rad*sc2, cy+(h2.y*Math.cos(tilt)-rz2*Math.sin(tilt))*rad*sc2);
                    }
                }
            }
            ctx.stroke(); ctx.setLineDash([]);

            // Lat/Lon wireframe ellipses
            ctx.strokeStyle = rgba(0.07); ctx.setLineDash([3,10]);
            for(let i=0; i<6; i++) {
                let py2 = (i/6)*2-1, pr = Math.sqrt(1-py2*py2);
                if(pr>0) { ctx.beginPath(); ctx.ellipse(cx, cy+py2*rad, pr*rad, pr*rad*0.35, 0, 0, Math.PI*2); ctx.stroke(); }
            }
            ctx.setLineDash([]);

            // Laser scan line
            let laserPos = (time%8000)/8000;
            let ly = cy - rad + (rad*2*laserPos);
            let scanR = Math.sqrt(Math.max(0, rad*rad - Math.pow(ly-cy,2)));
            if(scanR>0) {
                ctx.beginPath(); ctx.moveTo(cx-scanR,ly); ctx.lineTo(cx+scanR,ly);
                ctx.strokeStyle = rgba(0.4); ctx.setLineDash([2,2]); ctx.lineWidth=1; ctx.stroke();
                ctx.setLineDash([]);
            }

            // Draw all dots
            for(let i=0; i<dots.length; i++) {
                let dot = dots[i];
                let rZ = dot.x*Math.sin(rotation)+dot.z*Math.cos(rotation);
                let tY = dot.y*Math.cos(tilt)-rZ*Math.sin(tilt);
                let tZ = dot.y*Math.sin(tilt)+rZ*Math.cos(tilt);
                let scale = 300/(300+tZ*150);
                let px = cx+(dot.x*Math.cos(rotation)-dot.z*Math.sin(rotation))*rad*scale;
                let py = cy+tY*rad*scale;
                let isBehind = tZ>0.1;
                let alpha = Math.max(0,(1-tZ)*0.8); if(isBehind) alpha*=0.1;

                if(dot.isHub) {
                    let hc = dot.hubColor;
                    ctx.fillStyle = `rgba(${hc},${isBehind?0.2:1})`;
                    if(!isBehind) { ctx.shadowBlur=30*scale; ctx.shadowColor=`rgba(${hc},1)`; }
                    ctx.fillRect(px-dot.size*scale, py-dot.size*scale, dot.size*2*scale, dot.size*2*scale);
                    if(!isBehind) {
                        ctx.fillStyle="#fff"; ctx.fillRect(px-scale*0.6,py-scale*0.6,1.2*scale,1.2*scale);
                    }
                    ctx.shadowBlur=0;
                    if(!isBehind) {
                        // Rotating scan box
                        ctx.save(); ctx.translate(px,py); ctx.rotate(time/1200);
                        ctx.strokeStyle=`rgba(${hc},0.7)`; ctx.lineWidth=1.2;
                        ctx.strokeRect(-8*scale,-8*scale,16*scale,16*scale); ctx.restore();
                        // Pulse rings
                        [0,0.5].forEach(off=>{
                            let p=((time/2500)+off)%1;
                            ctx.beginPath(); ctx.arc(px,py,(2+p*28)*scale,0,Math.PI*2);
                            ctx.strokeStyle=`rgba(${hc},${0.3*(1-p)})`; ctx.lineWidth=1; ctx.stroke();
                        });
                    }
                } else {
                    ctx.fillStyle = dot.land ? `rgba(255,255,255,${alpha})` : `rgba(37,99,235,${alpha*0.4})`;
                    ctx.fillRect(px-scale*0.8,py-scale*0.8,1.6*scale,1.6*scale);
                }
            }

            // Shooting-star arcs with tails
            for(let i=signalsArcs.length-1; i>=0; i--) {
                let arc=signalsArcs[i]; arc.progress+=arc.speed;
                if(arc.progress>1) { signalsArcs[i]=makeArc(dots); continue; }
                let s=arc.start, e=arc.end;
                let omega=Math.acos(Math.max(-1,Math.min(1,s.x*e.x+s.y*e.y+s.z*e.z)));
                let sinO=Math.sin(omega);
                let s0=(sinO<1e-4?(1-arc.progress):Math.sin((1-arc.progress)*omega)/sinO);
                let s1=(sinO<1e-4?arc.progress:Math.sin(arc.progress*omega)/sinO);
                let elev=1+Math.sin(arc.progress*Math.PI)*omega*0.12;
                let pX=(s.x*s0+e.x*s1)*elev, pY=(s.y*s0+e.y*s1)*elev, pZ=(s.z*s0+e.z*s1)*elev;
                let rZ=pX*Math.sin(rotation)+pZ*Math.cos(rotation);
                let tZ=pY*Math.sin(tilt)+rZ*Math.cos(tilt);
                let sc=300/(300+tZ*150);
                let px=cx+(pX*Math.cos(rotation)-pZ*Math.sin(rotation))*rad*sc;
                let py=cy+(pY*Math.cos(tilt)-rZ*Math.sin(tilt))*rad*sc;
                arc.history.push({x:px,y:py,tZ});
                if(arc.history.length>30) arc.history.shift();
                // Tail
                if(arc.history.length>1 && tZ<0.2) {
                    ctx.beginPath(); ctx.moveTo(arc.history[0].x,arc.history[0].y);
                    for(let j=1;j<arc.history.length;j++) ctx.lineTo(arc.history[j].x,arc.history[j].y);
                    ctx.strokeStyle=`rgba(${arc.color},${Math.max(0,(1-tZ)*0.45)})`;
                    ctx.lineWidth=(tZ<0.2?1.8:0.6)*sc; ctx.shadowBlur=4*sc; ctx.shadowColor=`rgba(${arc.color},0.5)`;
                    ctx.stroke(); ctx.shadowBlur=0;
                }
                // Head
                if(tZ<0.2) {
                    ctx.beginPath(); ctx.arc(px,py,2.5*sc,0,Math.PI*2);
                    ctx.fillStyle="#fff"; ctx.shadowBlur=14*sc; ctx.shadowColor=`rgba(${arc.color},1)`;
                    ctx.fill(); ctx.shadowBlur=0;
                }
            }
        }

        // =============================================================
        // 2. CYBER SECURITY → LIVE CIRCUIT BOARD  (CIRCUITS)
        //    Concept: PCB mother board — data packets flowing along glowing traces
        // =============================================================
        else if (atmosphere === 'CIRCUITS') {
            ctx.save(); ctx.translate(cx, cy);

            // PCB board grid (substrate)
            ctx.strokeStyle = rgba(0.05); ctx.lineWidth = 1;
            for(let x=-480; x<=480; x+=40) { ctx.beginPath(); ctx.moveTo(x,-480); ctx.lineTo(x,480); ctx.stroke(); }
            for(let y=-480; y<=480; y+=40) { ctx.beginPath(); ctx.moveTo(-480,y); ctx.lineTo(480,y); ctx.stroke(); }

            // Circuit trace definitions (horizontal + vertical segments)
            const traces = [
                {pts:[[-420,-200],[-200,-200],[-200,-40],[0,-40]],  c:"14,165,233"},
                {pts:[[0,-40],[0,80],[200,80],[200,200],[420,200]],  c:"16,185,129"},
                {pts:[[-420,80],[-120,80],[-120,-120],[120,-120],[120,80],[300,80]], c:"245,158,11"},
                {pts:[[420,-80],[200,-80],[200,-200],[40,-200]],     c:"232,121,249"},
                {pts:[[-200,200],[-40,200],[-40,320],[200,320],[200,200]], c:"244,63,94"},
                {pts:[[0,320],[0,420]],                              c:"99,102,241"},
                {pts:[[-420,-80],[-280,-80],[-280,200],[-120,200]], c:"56,189,248"},
            ];

            // Draw traces + animated packets
            traces.forEach((trace, ti) => {
                // Draw the glowing trace line
                ctx.strokeStyle = `rgba(${trace.c},0.25)`; ctx.lineWidth = 6;
                ctx.beginPath(); ctx.moveTo(trace.pts[0][0], trace.pts[0][1]);
                for(let p=1; p<trace.pts.length; p++) ctx.lineTo(trace.pts[p][0], trace.pts[p][1]);
                ctx.stroke();
                ctx.strokeStyle = `rgba(${trace.c},0.6)`; ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(trace.pts[0][0], trace.pts[0][1]);
                for(let p=1; p<trace.pts.length; p++) ctx.lineTo(trace.pts[p][0], trace.pts[p][1]);
                ctx.stroke();

                // Compute total trace length for packet animation
                let totalLen = 0;
                for(let p=1; p<trace.pts.length; p++) {
                    totalLen += Math.hypot(trace.pts[p][0]-trace.pts[p-1][0], trace.pts[p][1]-trace.pts[p-1][1]);
                }
                // Animated data packet
                let prog = ((frame * 0.008 + ti * 0.14) % 1);
                let tDist = prog * totalLen, consumed = 0;
                for(let p=1; p<trace.pts.length; p++) {
                    let segLen = Math.hypot(trace.pts[p][0]-trace.pts[p-1][0], trace.pts[p][1]-trace.pts[p-1][1]);
                    if(tDist <= consumed + segLen) {
                        let t2 = (tDist - consumed) / segLen;
                        let px = trace.pts[p-1][0] + (trace.pts[p][0]-trace.pts[p-1][0])*t2;
                        let py = trace.pts[p-1][1] + (trace.pts[p][1]-trace.pts[p-1][1])*t2;
                        ctx.fillStyle = `rgba(${trace.c},1)`; ctx.shadowBlur = 20; ctx.shadowColor = `rgba(${trace.c},1)`;
                        ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI*2); ctx.fill();
                        ctx.fillStyle = "#fff"; ctx.shadowBlur = 0;
                        ctx.beginPath(); ctx.arc(px, py, 3, 0, Math.PI*2); ctx.fill();
                        break;
                    }
                    consumed += segLen;
                }
                // Node dots at trace points
                trace.pts.forEach(pt => {
                    ctx.fillStyle = `rgba(${trace.c},0.8)`;
                    ctx.beginPath(); ctx.arc(pt[0],pt[1],5,0,Math.PI*2); ctx.fill();
                    // Rotating square node marker
                    ctx.save(); ctx.translate(pt[0],pt[1]); ctx.rotate(frame*0.02);
                    ctx.strokeStyle=`rgba(${trace.c},0.5)`; ctx.strokeRect(-7,-7,14,14);
                    ctx.restore();
                });
            });

            // Central CPU Core
            let cpuPulse = 0.7 + Math.sin(frame*0.08)*0.3;
            ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${cpuPulse})`;
            ctx.shadowBlur = 40; ctx.shadowColor = color;
            ctx.fillRect(-30,-30,60,60);
            ctx.fillStyle = "#fff"; ctx.fillRect(-12,-12,24,24);
            ctx.shadowBlur = 0;
            ctx.font = "8px monospace"; ctx.fillStyle = rgba(0.5);
            ctx.fillText("CPU", -10, 4);

            // Floating binary bits
            for(let i=0; i<20; i++) {
                let bx = Math.sin(frame*0.02+i*1.3)*350, by = Math.cos(frame*0.015+i*0.9)*350;
                ctx.font = "10px monospace"; ctx.fillStyle = rgba(0.2);
                ctx.fillText(i%2?"1":"0", bx, by);
            }

            // HUD labels
            ctx.font="11px monospace"; ctx.fillStyle=rgba(0.4);
            ctx.fillText("KERNEL_TRACE_ACTIVE", -420, -440);
            ctx.fillText(`PACK_RATE: ${(frame%60+40)}MB/s`, 260, -440);
            ctx.restore();
        }

        // =============================================================
        // 3. IP SURVEILLANCE → 3D VOLUMETRIC FACE SCAN  (SCANNER)
        //    Concept: Biometric face mesh with depth layers and landmark scan
        // =============================================================
        else if (atmosphere === 'SCANNER') {
            ctx.save(); ctx.translate(cx, cy);

            // Outer target reticle rings
            [420, 310, 200, 100].forEach((r,i) => {
                ctx.strokeStyle = rgba(0.06 + i*0.03); ctx.lineWidth = 1;
                ctx.setLineDash(i%2===0?[]:[4,12]);
                ctx.beginPath(); ctx.arc(0,0,r,0,Math.PI*2); ctx.stroke();
            });
            ctx.setLineDash([]);

            // Fine-grid reticle overlay (tilt 45°)
            ctx.save(); ctx.rotate(Math.PI/4);
            ctx.strokeStyle = rgba(0.04); ctx.lineWidth = 0.5;
            for(let x=-420; x<=420; x+=30) {
                ctx.beginPath(); ctx.moveTo(x,-420); ctx.lineTo(x,420); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(-420,x); ctx.lineTo(420,x); ctx.stroke();
            }
            ctx.restore();

            // Crosshairs
            ctx.strokeStyle = rgba(0.12); ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(-450,0); ctx.lineTo(450,0); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(0,-450); ctx.lineTo(0,450); ctx.stroke();

            // ---- 3D FACE WIREFRAME MESH ----
            // Face outline (ovoid)
            ctx.strokeStyle = rgba(0.5); ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.ellipse(0, 20, 140, 185, 0, 0, Math.PI*2); ctx.stroke();
            // Forehead curve
            ctx.beginPath(); ctx.ellipse(0,-90, 130, 70, 0, Math.PI, 0); ctx.stroke();

            // Face depth contour rings (like a topography map)
            const scanY = Math.sin(frame*0.025)*160;  // oscillating scan plane
            for(let y=-160; y<=180; y+=28) {
                let spread = Math.sqrt(Math.max(0, 185*185 - (y-20)*(y-20))) / 185;
                let rx = spread * 140;
                if(rx < 5) continue;
                let distFromScan = Math.abs(y - scanY);
                let isNearScan = distFromScan < 30;
                ctx.strokeStyle = isNearScan ? `rgba(${rgb.r},${rgb.g},${rgb.b},${1-(distFromScan/30)*0.7})` : rgba(0.12);
                ctx.lineWidth = isNearScan ? 2 : 0.8;
                ctx.beginPath(); ctx.ellipse(0, y, rx, 15, 0, 0, Math.PI*2); ctx.stroke();
            }

            // Scan Beam (bright horizontal slab)
            let sBeam = ctx.createLinearGradient(0, scanY-15, 0, scanY+15);
            sBeam.addColorStop(0, "transparent");
            sBeam.addColorStop(0.5, rgba(0.35));
            sBeam.addColorStop(1, "transparent");
            ctx.fillStyle = sBeam; ctx.fillRect(-160, scanY-15, 320, 30);

            // Feature landmark points (eyes, nose, mouth, temples)
            const landmarks = [
                // Eyes
                {x:-55, y:-40}, {x:55, y:-40},
                // Nose bridge, tip
                {x:0, y:-10}, {x:0, y:55},
                // Mouth corners
                {x:-45, y:90}, {x:45, y:90}, {x:0, y:110},
                // Ears
                {x:-140, y:20}, {x:140, y:20},
                // Chin
                {x:0, y:195},
                // Forehead
                {x:-70, y:-110}, {x:70, y:-110},
                // Cheekbones
                {x:-115, y:40}, {x:115, y:40},
            ];

            landmarks.forEach((lm, i) => {
                let active = Math.abs(lm.y - scanY) < 35;
                ctx.fillStyle = active ? "#fff" : rgba(0.4);
                ctx.shadowBlur = active ? 12 : 0; ctx.shadowColor = color;
                ctx.beginPath(); ctx.arc(lm.x, lm.y, active?5:3, 0, Math.PI*2); ctx.fill();
                ctx.shadowBlur = 0;
                if(active) {
                    ctx.strokeStyle = rgba(0.6); ctx.lineWidth=1;
                    ctx.strokeRect(lm.x-10, lm.y-10, 20, 20);
                }
                // Connection lines between adjacent face landmarks
                if(i < landmarks.length-1 && i !== 1 && i !== 4) {
                    ctx.strokeStyle = rgba(0.08); ctx.lineWidth=0.5;
                    ctx.beginPath(); ctx.moveTo(lm.x,lm.y); ctx.lineTo(landmarks[i+1].x, landmarks[i+1].y); ctx.stroke();
                }
            });

            // Confidence / biometric overlays
            let conf = 60 + Math.floor((Math.sin(frame*0.02)+1)*19);
            ctx.font="bold 12px monospace"; ctx.fillStyle=rgba(0.7);
            ctx.fillText(`BIO_MATCH: ${conf}%`, 180, -200);
            ctx.fillText(`DEPTH_SCAN: ${Math.floor(Math.abs(scanY)+50)}mm`, 180,-182);
            ctx.fillText(`LANDMARKS: ${landmarks.length}pts`, -420,-200);
            ctx.fillStyle= conf>75 ? "#10B981" : "#F59E0B";
            ctx.fillText(conf>75 ? "▶ IDENTITY_CONFIRMED":"▶ PROCESSING...", -420,-182);

            // HUD corner markers
            [[-430,-430],[-430,430],[430,-430],[430,430]].forEach(([hx,hy])=>{
                ctx.strokeStyle=rgba(0.3); ctx.lineWidth=2;
                ctx.beginPath();
                ctx.moveTo(hx, hy+(hy<0?25:-25)); ctx.lineTo(hx,hy); ctx.lineTo(hx+(hx<0?25:-25),hy);
                ctx.stroke();
            });

            ctx.restore();
        }

        // =============================================================
        // 4. ACTIVE ALARMS → HEXAGONAL ELECTROMAGNETIC FORCEFIELD  (PULSE)
        //    Concept: Hex forcefield cells activate like shields, lightning strikes
        // =============================================================
        else if (atmosphere === 'PULSE') {
            ctx.save(); ctx.translate(cx, cy);

            // --- Hexagonal grid forcefield ---
            const hSize = 38;
            const hW = hSize * 2;
            const hH = Math.sqrt(3) * hSize;
            for(let row=-6; row<=6; row++) {
                for(let col=-6; col<=6; col++) {
                    let hx = col * hW * 0.75;
                    let hy = row * hH + (col%2===0 ? 0 : hH*0.5);
                    let dist = Math.sqrt(hx*hx + hy*hy);
                    if(dist > 430) continue;

                    // Wave ripple activation from center
                    let wave = (Math.sin(dist*0.015 - frame*0.07) + 1) / 2;
                    let active = wave > 0.72;
                    let hotspot = wave > 0.90;

                    ctx.beginPath();
                    for(let k=0; k<6; k++) {
                        let angle = k*Math.PI/3 - Math.PI/6;
                        ctx.lineTo(hx + Math.cos(angle)*(hSize-2), hy + Math.sin(angle)*(hSize-2));
                    }
                    ctx.closePath();

                    if(hotspot) {
                        ctx.fillStyle = rgba(0.3); ctx.shadowBlur=15; ctx.shadowColor=color;
                        ctx.fill(); ctx.shadowBlur=0;
                        ctx.strokeStyle = "#fff"; ctx.lineWidth=2.5;
                    } else if(active) {
                        ctx.fillStyle = rgba(0.12);
                        ctx.fill();
                        ctx.strokeStyle = color; ctx.lineWidth=1.5;
                    } else {
                        ctx.strokeStyle = rgba(0.06); ctx.lineWidth=0.5;
                    }
                    ctx.stroke();
                }
            }

            // Central command node (pulsing)
            let cPulse = 0.6 + Math.sin(frame*0.1)*0.4;
            let cg = ctx.createRadialGradient(0,0,0,0,0,50);
            cg.addColorStop(0,"#fff"); cg.addColorStop(0.3, color); cg.addColorStop(1,"transparent");
            ctx.fillStyle=cg; ctx.globalAlpha=cPulse;
            ctx.beginPath(); ctx.arc(0,0,50,0,Math.PI*2); ctx.fill();
            ctx.globalAlpha=1;

            // Lightning arcs from center (random, flickering)
            if(frame % 8 < 3) {
                let boltCount = 2 + Math.floor(Math.random()*3);
                for(let b=0; b<boltCount; b++) {
                    let bAng = Math.random()*Math.PI*2;
                    let bLen = 150 + Math.random()*200;
                    ctx.strokeStyle = "rgba(255,255,255,0.7)"; ctx.lineWidth=1.5;
                    ctx.shadowBlur=10; ctx.shadowColor="#fff";
                    ctx.beginPath(); ctx.moveTo(0,0);
                    let steps=6, lx=0, ly=0;
                    for(let s=1; s<=steps; s++) {
                        let prog = s/steps;
                        lx = Math.cos(bAng)*bLen*prog + (Math.random()-0.5)*30;
                        ly = Math.sin(bAng)*bLen*prog + (Math.random()-0.5)*30;
                        ctx.lineTo(lx, ly);
                    }
                    ctx.stroke(); ctx.shadowBlur=0;
                }
            }

            // Expanding alert rings
            for(let i=0; i<4; i++) {
                let p = (frame*0.007 + i*0.25)%1;
                ctx.strokeStyle=color; ctx.globalAlpha=(1-p)*(i===0?0.8:0.4); ctx.lineWidth=3-i*0.5;
                ctx.beginPath(); ctx.arc(0,0,p*430,0,Math.PI*2); ctx.stroke();
            }
            ctx.globalAlpha=1;

            // HUD labels
            ctx.font="12px monospace"; ctx.fillStyle=rgba(0.45);
            ctx.fillText("EMF_SHIELD: ACTIVE", -420,-440);
            ctx.fillText(`CELLS_HOT: ${Math.floor((Math.sin(frame*0.05)+1)*30+40)}`, 220,-440);
            ctx.fillText(`CHARGE: ${Math.floor((Math.sin(frame*0.03)+1)*50+50)}%`, 220,450);

            ctx.restore();
        }

        // =============================================================
        // 5. PROFESSIONAL TRAINING → NEURAL KNOWLEDGE GRAPH  (CRYSTAL)
        //    Concept: Living network of knowledge nodes, signals flow along paths
        // =============================================================
        else if (atmosphere === 'CRYSTAL') {
            ctx.save(); ctx.translate(cx, cy);

            // 20 knowledge topic nodes placed in concentric rings
            const nodeColors = [
                "14,165,233","16,185,129","245,158,11","244,63,94","99,102,241",
                "232,121,249","6,182,212","251,191,36","167,139,250","52,211,153"
            ];
            const nodeRings = [
                [{label:"OSINT",a:0},{label:"SIGINT",a:72},{label:"HUMINT",a:144},{label:"GEOINT",a:216},{label:"MASINT",a:288}],
                [{label:"CTF",a:36},{label:"PENTEST",a:108},{label:"FORENSICS",a:180},{label:"SIEM",a:252},{label:"MALWARE",a:324}],
                [{label:"THREAT",a:18},{label:"SOC",a:90},{label:"RED TEAM",a:162},{label:"BLUE TEAM",a:234},{label:"ZERO DAY",a:306}],
            ];
            const radii=[170, 300, 420];
            const allNodes = [];
            nodeRings.forEach((ring,ri) => ring.forEach((n,ni) => {
                let a = n.a * Math.PI/180 + frame*0.003*(ri%2===0?1:-1);
                allNodes.push({
                    x: Math.cos(a)*radii[ri], y: Math.sin(a)*radii[ri],
                    label:n.label, color:nodeColors[(ri*5+ni)%nodeColors.length],
                    r:ri, ni
                });
            }));

            // Central "CORE" master node
            allNodes.push({x:0,y:0,label:"CORE",color:`${rgb.r},${rgb.g},${rgb.b}`,r:-1});

            // Draw knowledge edges (connections)
            // Core to ring-0
            allNodes.filter(n=>n.r===0).forEach(n => {
                ctx.strokeStyle = rgba(0.15); ctx.lineWidth=1;
                ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(n.x,n.y); ctx.stroke();
            });
            // Ring-0 to ring-1 connections
            allNodes.filter(n=>n.r===0).forEach((n,i) => {
                let m = allNodes.filter(nd=>nd.r===1)[i%5];
                ctx.strokeStyle = rgba(0.08); ctx.lineWidth=0.7;
                ctx.beginPath(); ctx.moveTo(n.x,n.y); ctx.lineTo(m.x,m.y); ctx.stroke();
            });
            // Ring-1 to ring-2 connections
            allNodes.filter(n=>n.r===1).forEach((n,i) => {
                let m = allNodes.filter(nd=>nd.r===2)[i%5];
                ctx.strokeStyle = rgba(0.05); ctx.lineWidth=0.5;
                ctx.beginPath(); ctx.moveTo(n.x,n.y); ctx.lineTo(m.x,m.y); ctx.stroke();
            });

            // Animated knowledge signals flowing from core outward
            for(let i=0; i<allNodes.filter(n=>n.r===0).length; i++) {
                let target = allNodes.filter(n=>n.r===0)[i];
                let prog = (frame*0.006 + i*0.2)%1;
                let px = target.x*prog, py = target.y*prog;
                ctx.fillStyle = `rgba(${target.color},${1-prog*0.5})`;
                ctx.shadowBlur=8; ctx.shadowColor=`rgba(${target.color},0.8)`;
                ctx.beginPath(); ctx.arc(px,py,5*(1-prog*0.3),0,Math.PI*2); ctx.fill();
                ctx.shadowBlur=0;
            }
            // Signals ring-0 → ring-1
            allNodes.filter(n=>n.r===0).forEach((n,i) => {
                let target = allNodes.filter(nd=>nd.r===1)[i%5];
                let prog = (frame*0.004 + i*0.2)%1;
                let px = n.x + (target.x-n.x)*prog, py = n.y + (target.y-n.y)*prog;
                ctx.fillStyle = `rgba(${target.color},${0.8-prog*0.3})`;
                ctx.beginPath(); ctx.arc(px,py,4,0,Math.PI*2); ctx.fill();
            });

            // Draw all nodes
            allNodes.forEach(n => {
                let isCore = n.r===-1;
                let nodeR = isCore ? 22 : (n.r===0?14:n.r===1?11:8);
                // Node glow
                let ng = ctx.createRadialGradient(n.x,n.y,0,n.x,n.y,nodeR*2);
                ng.addColorStop(0, `rgba(${n.color},0.3)`); ng.addColorStop(1,"transparent");
                ctx.fillStyle=ng; ctx.beginPath(); ctx.arc(n.x,n.y,nodeR*2,0,Math.PI*2); ctx.fill();
                // Node body
                ctx.fillStyle = isCore?"#fff":`rgba(${n.color},1)`;
                ctx.shadowBlur=isCore?30:15; ctx.shadowColor=`rgba(${n.color},0.8)`;
                ctx.beginPath(); ctx.arc(n.x,n.y,nodeR,0,Math.PI*2); ctx.fill();
                ctx.shadowBlur=0;
                // Node inner core
                if(!isCore) {
                    ctx.fillStyle="#fff"; ctx.beginPath(); ctx.arc(n.x,n.y,nodeR*0.4,0,Math.PI*2); ctx.fill();
                }
                // Label for outer rings only
                if(n.r>0) {
                    ctx.font=`bold ${n.r===1?9:8}px monospace`; ctx.fillStyle=`rgba(${n.color},0.8)`;
                    ctx.fillText(n.label, n.x+nodeR+3, n.y+3);
                }
            });
            // Core label
            ctx.font="bold 9px monospace"; ctx.fillStyle="rgba(0,0,0,0.8)";
            ctx.fillText("CORE", -12, 3);

            ctx.font="11px monospace"; ctx.fillStyle=rgba(0.35);
            ctx.fillText(`NODES_ACTIVE: ${allNodes.length}`, -420, -440);
            ctx.fillText("KNOWLEDGE_NET_v5", 200, -440);

            ctx.restore();
        }

        raf = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(raf);
  }, [atmosphere, color]);

  return (
    <canvas 
      ref={canvasRef} 
      width="1000" height="1000" 
      style={{ width:"100%", height:"100%", filter:"drop-shadow(0 0 60px rgba(0,0,0,0.9))" }} 
    />
  );
};

export default TacticalVisualizer;
